import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { MapPin, User, Search, Bell, LogOut, ChevronDown } from 'lucide-react';
import LocationModal from './LocationModal';
import LoginModal from './LoginModal';

export default function Navbar() {
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('Bhubaneswar');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({ shows: [], parties: [], camps: [], artists: [] });
  const [isSearchSearching, setIsSearchSearching] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [user, setUser] = useState(null);
  
  const navigate = useNavigate();
  const searchRef = useRef(null);

  const loadUser = () => {
    const userDataStr = localStorage.getItem('user_data');
    if (userDataStr) {
      setUser(JSON.parse(userDataStr));
    } else {
      setUser(null);
    }
  };

  useEffect(() => {
    loadUser();
    window.addEventListener('auth_changed', loadUser);
    
    const handleOpenLogin = () => setIsLoginModalOpen(true);
    const handleOpenLocation = () => setIsLocationOpen(true);
    
    window.addEventListener('openLoginModal', handleOpenLogin);
    window.addEventListener('openLocationModal', handleOpenLocation);
    
    return () => {
      window.removeEventListener('auth_changed', loadUser);
      window.removeEventListener('openLoginModal', handleOpenLogin);
      window.removeEventListener('openLocationModal', handleOpenLocation);
    };
  }, []);

  useEffect(() => {
    const updateLocation = () => {
      const storedLocation = localStorage.getItem('selected_district_name');
      if (storedLocation) {
        setSelectedLocation(storedLocation);
      } else {
        setSelectedLocation('Bhubaneswar');
      }
    };
    
    updateLocation();
    window.addEventListener('locationChanged', updateLocation);
    
    return () => window.removeEventListener('locationChanged', updateLocation);
  }, []);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await fetch('/api/notifications', {
          headers: {
            'Client-ID': 'lokanatya_client',
            'Client-Secret': 'super_secret_key_123',
            'Accept': 'application/json'
          }
        });
        const data = await response.json();
        if (data.status === 'success') {
          setNotifications(data.data || []);
        }
      } catch (error) {
        console.error('Error fetching notifications:', error);
      }
    };

    fetchNotifications();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('user_data');
    localStorage.removeItem('auth_token');
    setUser(null);
    window.dispatchEvent(new Event('auth_changed'));
    navigate('/');
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults({ shows: [], parties: [], camps: [], artists: [] });
      setIsSearchOpen(false);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setIsSearchSearching(true);
        const res = await fetch(`/api/global_search?q=${encodeURIComponent(searchQuery.trim())}`, {
          headers: {
            'Client-ID': 'lokanatya_client',
            'Client-Secret': 'super_secret_key_123',
            'Accept': 'application/json'
          }
        });
        const json = await res.json();
        if (json.status === 'success' && json.data) {
          setSearchResults(json.data);
          setIsSearchOpen(true);
        }
      } catch (err) {
        console.error('Global search error:', err);
      } finally {
        setIsSearchSearching(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setIsSearchOpen(false);
      navigate(`/shows?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const getImageUrl = (img) => {
    if (!img) return 'https://upload.wikimedia.org/wikipedia/commons/4/41/Placeholder_view_vector.svg';
    if (img.startsWith('http://') || img.startsWith('https://')) return img;
    if (img.startsWith('/')) return img;
    return '/' + img;
  };

  const hasAnyResults = 
    (searchResults.shows && searchResults.shows.length > 0) ||
    (searchResults.parties && searchResults.parties.length > 0) ||
    (searchResults.camps && searchResults.camps.length > 0) ||
    (searchResults.artists && searchResults.artists.length > 0);

  return (
    <>
      <nav className="sticky top-0 z-[60] bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-xs">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16 md:h-20 gap-2 sm:gap-4">
            
            {/* Left: Brand Logo */}
            <div className="flex items-center gap-2 shrink-0">
              <Link to="/" className="flex items-center gap-2 group">
                <img 
                  src="https://lokanatya.com/logo.png" 
                  alt="Lokanatya" 
                  className="h-8 sm:h-10 w-auto object-contain group-hover:scale-105 transition-transform duration-300"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    if (e.target.nextElementSibling) {
                      e.target.nextElementSibling.style.display = 'flex';
                    }
                  }}
                />
                <div className="hidden items-center gap-2">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-2xl bg-gradient-to-tr from-red-600 via-rose-500 to-amber-500 flex items-center justify-center text-white shadow-md shadow-red-500/20">
                    <span className="font-black text-base sm:text-xl tracking-tighter">L</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="font-extrabold text-base sm:text-xl text-gray-900 tracking-tight leading-none">
                      Lokanatya<span className="text-red-600">.</span>
                    </span>
                    <span className="text-[9px] font-bold text-gray-400 tracking-widest uppercase mt-0.5">Jatra Tickets</span>
                  </div>
                </div>
              </Link>
            </div>

            {/* Center: Global Live Search Bar */}
            <div ref={searchRef} className="hidden sm:flex flex-1 max-w-md mx-2 md:mx-6 relative">
              <form onSubmit={handleSearchSubmit} className="w-full">
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400 group-focus-within:text-red-500 transition-colors">
                    {isSearchSearching ? (
                      <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <Search className="h-4 w-4" />
                    )}
                  </div>
                  <input
                    type="text"
                    value={searchQuery}
                    onFocus={() => { if (searchQuery.trim()) setIsSearchOpen(true); }}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search shows, gananatya, camps, artists..."
                    className="w-full bg-gray-50/80 hover:bg-gray-100/70 focus:bg-white text-gray-800 text-xs sm:text-sm font-medium pl-10 pr-8 py-2 rounded-full border border-gray-200 focus:border-red-400 focus:ring-2 focus:ring-red-100 outline-none transition-all placeholder:text-gray-400 shadow-2xs"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => { setSearchQuery(''); setIsSearchOpen(false); }}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 text-xs font-bold"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </form>

              {/* Categorized Global Search Live Dropdown Panel */}
              {isSearchOpen && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-[100] max-h-[80vh] overflow-y-auto divide-y divide-gray-100 animate-in fade-in duration-150">
                  {!hasAnyResults && !isSearchSearching ? (
                    <div className="p-4 text-center text-xs text-gray-400 font-bold uppercase tracking-wider">
                      No matching shows, gananatya, or camps found
                    </div>
                  ) : (
                    <>
                      {/* Section 1: Shows / Jatra Plays */}
                      {searchResults.shows && searchResults.shows.length > 0 && (
                        <div className="p-2.5">
                          <div className="text-[10px] font-black text-red-600 uppercase tracking-widest px-2 mb-1.5 flex items-center gap-1">
                            <span>🎭</span> SHOWS & JATRA
                          </div>
                          <div className="space-y-1">
                            {searchResults.shows.map(show => (
                              <Link
                                key={show.product_id}
                                to={`/shows/${show.product_id}/seats`}
                                onClick={() => setIsSearchOpen(false)}
                                className="flex items-center gap-2.5 p-1.5 rounded-xl hover:bg-gray-50 transition-colors"
                              >
                                <img
                                  src={getImageUrl(show.image)}
                                  alt={show.jatra_name}
                                  className="w-9 h-11 object-cover rounded-lg shrink-0 bg-gray-100 border border-gray-100"
                                />
                                <div className="min-w-0 flex-1">
                                  <div className="text-xs font-bold text-gray-900 truncate">
                                    {show.jatra_name}
                                  </div>
                                  <div className="text-[10px] text-gray-400 font-medium truncate mt-0.5">
                                    {show.cat_name} • {show.jatra_location || 'Odisha'}
                                  </div>
                                </div>
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Section 2: Gananatya / Parties */}
                      {searchResults.parties && searchResults.parties.length > 0 && (
                        <div className="p-2.5">
                          <div className="text-[10px] font-black text-amber-600 uppercase tracking-widest px-2 mb-1.5 flex items-center gap-1">
                            <span>🎪</span> GANANATYA & PARTIES
                          </div>
                          <div className="space-y-1">
                            {searchResults.parties.map(party => (
                              <Link
                                key={party.id}
                                to={`/shows?category=${party.id}`}
                                onClick={() => setIsSearchOpen(false)}
                                className="flex items-center gap-2.5 p-1.5 rounded-xl hover:bg-amber-50/50 transition-colors"
                              >
                                <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 font-bold flex items-center justify-center text-xs shrink-0 border border-amber-200">
                                  {party.cat_name ? party.cat_name.charAt(0) : 'P'}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="text-xs font-extrabold text-gray-900 truncate">
                                    {party.cat_name}
                                  </div>
                                  <div className="text-[9px] text-amber-600 font-bold uppercase tracking-wider">
                                    Party / Opera Group
                                  </div>
                                </div>
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Section 3: Camps */}
                      {searchResults.camps && searchResults.camps.length > 0 && (
                        <div className="p-2.5">
                          <div className="text-[10px] font-black text-blue-600 uppercase tracking-widest px-2 mb-1.5 flex items-center gap-1">
                            <span>⛺</span> CAMPS & LOCATIONS
                          </div>
                          <div className="space-y-1">
                            {searchResults.camps.map(camp => (
                              <Link
                                key={camp.id}
                                to="/camps"
                                onClick={() => setIsSearchOpen(false)}
                                className="flex items-center gap-2.5 p-1.5 rounded-xl hover:bg-blue-50/50 transition-colors"
                              >
                                <div className="w-8 h-8 rounded-xl bg-blue-50 text-blue-600 font-bold flex items-center justify-center text-xs shrink-0 border border-blue-100">
                                  ⛺
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="text-xs font-bold text-gray-900 truncate">
                                    {camp.cat_name} Camp
                                  </div>
                                  <div className="text-[10px] text-gray-400 font-medium truncate">
                                    📍 {camp.camp_location || camp.district_name || 'Location'}
                                  </div>
                                </div>
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Section 4: Artists / Actors */}
                      {searchResults.artists && searchResults.artists.length > 0 && (
                        <div className="p-2.5">
                          <div className="text-[10px] font-black text-purple-600 uppercase tracking-widest px-2 mb-1.5 flex items-center gap-1">
                            <span>🌟</span> ARTISTS & ACTORS
                          </div>
                          <div className="space-y-1">
                            {searchResults.artists.map(artist => (
                              <Link
                                key={artist.id}
                                to={`/shows?search=${encodeURIComponent(artist.name)}`}
                                onClick={() => setIsSearchOpen(false)}
                                className="flex items-center gap-2.5 p-1.5 rounded-xl hover:bg-purple-50/50 transition-colors"
                              >
                                <img
                                  src={getImageUrl(artist.profile_image)}
                                  alt={artist.name}
                                  className="w-8 h-8 rounded-full object-cover shrink-0 border border-purple-100"
                                />
                                <div className="min-w-0 flex-1">
                                  <div className="text-xs font-bold text-gray-900 truncate">
                                    {artist.name}
                                  </div>
                                  <div className="text-[9px] text-purple-600 font-bold uppercase tracking-wider">
                                    Artist
                                  </div>
                                </div>
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Right Action Menu: Location + Notifications + Profile */}
            <div className="flex items-center gap-1.5 sm:gap-3">
              
              {/* Touch Responsive Location Pill Button */}
              <button
                onClick={() => setIsLocationOpen(true)}
                className="flex items-center gap-1 text-gray-800 bg-gray-100/90 hover:bg-gray-200/80 px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-full transition text-xs font-bold border border-gray-200/60 shadow-2xs shrink-0 cursor-pointer"
                aria-label="Select Location"
                title={`Location: ${selectedLocation}`}
              >
                <MapPin className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-red-500 shrink-0" />
                <span className="max-w-[75px] sm:max-w-[120px] truncate">{selectedLocation}</span>
                <ChevronDown className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-gray-400 shrink-0" />
              </button>
              
              {/* Notification Bell */}
              <div className="relative">
                <button 
                  onClick={() => {
                    if (window.innerWidth < 768) {
                      navigate('/notifications');
                    } else {
                      setIsNotificationOpen(!isNotificationOpen);
                    }
                  }}
                  className="relative p-1.5 sm:p-2 text-gray-600 hover:text-red-500 hover:bg-gray-100 rounded-full transition-all"
                  aria-label="Notifications"
                >
                  <Bell className="h-5 w-5" />
                  {notifications.length > 0 && (
                    <span className="absolute top-1 right-1 w-2 h-2 sm:w-2.5 sm:h-2.5 bg-red-500 rounded-full border-2 border-white animate-pulse"></span>
                  )}
                </button>
                
                {/* Notifications Dropdown */}
                {isNotificationOpen && (
                  <div className="absolute right-0 mt-3 w-72 sm:w-80 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50 py-2 animate-in fade-in slide-in-from-top-2 duration-200">
                    <div className="px-4 py-2.5 border-b border-gray-100 flex justify-between items-center">
                      <h3 className="font-bold text-gray-900 text-sm">Notifications</h3>
                    </div>
                    <div className="max-h-80 overflow-y-auto divide-y divide-gray-50">
                      {notifications.length > 0 ? (
                        notifications.map(notif => (
                          <div key={notif.id} className="px-4 py-3 hover:bg-gray-50 transition flex gap-3">
                            {notif.image ? (
                              <img src={notif.image} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" />
                            ) : (
                              <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center shrink-0">
                                <Bell size={16} className="text-red-500" />
                              </div>
                            )}
                            <div>
                              <h4 className="text-sm font-bold text-gray-900">{notif.title}</h4>
                              <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{notif.message}</p>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-8 text-center text-xs font-medium text-gray-400">
                          No new notifications
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Login / Profile Section (Desktop Only) */}
              {user ? (
                <div className="hidden md:flex items-center gap-2">
                  <Link 
                    to="/profile"
                    className="flex items-center gap-1.5 text-sm font-bold bg-gray-100 hover:bg-gray-200 text-gray-800 transition px-4 py-2 rounded-full"
                  >
                    <User className="h-4 w-4 text-red-500" />
                    <span>+91 {user.mobile ? user.mobile.substring(0, 4) + '...' : ''}</span>
                  </Link>
                  <button 
                    onClick={handleLogout}
                    className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-full transition"
                    title="Log out"
                  >
                    <LogOut size={16} />
                  </button>
                </div>
              ) : (
                <button 
                  onClick={() => setIsLoginModalOpen(true)}
                  className="hidden md:flex bg-gradient-to-r from-red-600 via-rose-500 to-pink-500 hover:from-red-700 hover:to-pink-600 text-white px-5 py-2 rounded-full font-semibold text-sm transition-all transform hover:scale-[1.03] active:scale-95 shadow-md shadow-red-500/25 items-center gap-1"
                >
                  Sign In
                </button>
              )}
            </div>

          </div>
        </div>
      </nav>

      {/* Modals */}
      <LocationModal 
        isOpen={isLocationOpen} 
        onClose={() => setIsLocationOpen(false)} 
        selectedDistrict={selectedLocation} 
      />

      <LoginModal 
        isOpen={isLoginModalOpen} 
        onClose={() => setIsLoginModalOpen(false)} 
      />
    </>
  );
}
